import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";

import styles from "./ContactUsPage.module.css";
import { IMAGES } from "../utils/GetImages";
import { getTranslation } from "../utils/translation";

export default function ContactUsPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    areaCode: "",
    phoneNumber: "",
    content: "",
  });
  const navigate = useNavigate();
  const [forceUpdate, setForceUpdate] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  const { i18n } = useTranslation();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const fromParam = (searchParams.get("from") || "HOME").toUpperCase();

  const ContactUsBackground = IMAGES["MainBackground.png"];

  useEffect(() => {
    const handleLanguageChange = () => {
      setForceUpdate((prev) => prev + 1);
    };

    i18n.on("languageChanged", handleLanguageChange);
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n]);

  // 等待翻譯資源載入
  useEffect(() => {
    if (i18n.isInitialized && i18n.store.data) {
      setIsReady(true);
    }
  }, [i18n.isInitialized, i18n.store.data]);

  // Base URL for the API - you may need to configure this based on your environment
  const baseUrl =
    "https://x4rau8a9vk.execute-api.ap-southeast-2.amazonaws.com/prod";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const sendEmailInquiry = async () => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${baseUrl}/email-inquiry`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          areaCode: form.areaCode,
          phoneNumber: form.phoneNumber,
          content: form.content,
          from: fromParam === "HOST" ? "HOST" : "HOME",
        }),
      });

      if (response.ok) {
        setSubmitStatus("success");
        // Reset form after successful submission
        setForm({
          name: "",
          email: "",
          areaCode: "",
          phoneNumber: "",
          content: "",
        });
      } else {
        setSubmitStatus("error");
        console.error("API Error:", response.status, response.statusText);
      }
    } catch (error) {
      setSubmitStatus("error");
      console.error("Network Error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const confirmed = window.confirm(
      getTranslation(i18n, "contactUsPage.confirmationBox")
    );
    if (!confirmed) {
      return;
    }

    sendEmailInquiry();
  };

  // 如果還沒準備好，顯示載入狀態
  if (!isReady) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
        }}
      >
        {getTranslation(i18n, "contactUsPage.loading") || "Loading..."}
      </div>
    );
  }

  return (
    <div
      className={styles["contact-container"]}
      style={{ backgroundImage: `url(${ContactUsBackground})` }}
      key={`${i18n.language}-${forceUpdate}`}
    >
      <div className={styles["contact-card"]}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h2 className={styles["contact-card-title"]}>
            {getTranslation(i18n, "contactUsPage.title")}
          </h2>
        </div>
        <p className={styles["subtitle"]}>
          {getTranslation(i18n, "contactUsPage.subtitle")}
        </p>

        {/* Status Messages */}
        {submitStatus === "error" && (
          <div
            className={styles["status-message"]}
            style={{ color: "red", marginBottom: "20px" }}
          >
            {getTranslation(i18n, "contactUsPage.errorMessage")}
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles["animated-form"]}>
          <label
            className={`${styles["form-label"]} ${styles["form-field-1"]}`}
          >
            {getTranslation(i18n, "contactUsPage.form.name")}
            <input
              className={`${styles["form-input"]} ${styles["animated-input"]}`}
              type="text"
              name="name"
              placeholder={getTranslation(
                i18n,
                "contactUsPage.form.namePlaceholder"
              )}
              value={form.name}
              onChange={handleChange}
              required
              disabled={isSubmitting}
            />
          </label>

          <label
            className={`${styles["form-label"]} ${styles["form-field-2"]}`}
          >
            {getTranslation(i18n, "contactUsPage.form.email")}
            <input
              className={`${styles["form-input"]} ${styles["animated-input"]}`}
              type="email"
              name="email"
              placeholder={getTranslation(
                i18n,
                "contactUsPage.form.emailPlaceholder"
              )}
              value={form.email}
              onChange={handleChange}
              required
              disabled={isSubmitting}
            />
          </label>

          <div
            className={`${styles["phone-container"]} ${styles["form-field-3"]}`}
          >
            <label className={styles["form-label"]}>
              {getTranslation(i18n, "contactUsPage.form.areaCode") ||
                "Area Code"}
              <input
                className={`${styles["form-input"]} ${styles["area-code-input"]} ${styles["animated-input"]}`}
                type="tel"
                name="areaCode"
                placeholder={
                  getTranslation(
                    i18n,
                    "contactUsPage.form.areaCodePlaceholder"
                  ) || "e.g. 614"
                }
                value={form.areaCode}
                onChange={handleChange}
                disabled={isSubmitting}
                maxLength="4"
              />
            </label>
            <label className={styles["form-label"]}>
              {getTranslation(i18n, "contactUsPage.form.phoneNumber") ||
                "Phone Number"}
              <input
                className={`${styles["form-input"]} ${styles["phone-number-input"]} ${styles["animated-input"]}`}
                type="tel"
                name="phoneNumber"
                placeholder={
                  getTranslation(
                    i18n,
                    "contactUsPage.form.phoneNumberPlaceholder"
                  ) || "e.g. 1234567"
                }
                value={form.phoneNumber}
                onChange={handleChange}
                disabled={isSubmitting}
              />
            </label>
          </div>

          <label
            className={`${styles["form-label"]} ${styles["form-field-4"]}`}
          >
            {getTranslation(i18n, "contactUsPage.form.message")}
            <textarea
              className={`${styles["form-textarea"]} ${styles["animated-input"]}`}
              name="content"
              placeholder={getTranslation(
                i18n,
                "contactUsPage.form.messagePlaceholder"
              )}
              value={form.content}
              onChange={handleChange}
              required
              disabled={isSubmitting}
            />
          </label>

          <button
            type="submit"
            className={`${styles["form-button"]} ${styles["animated-submit-button"]} ${styles["form-field-5"]}`}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? getTranslation(i18n, "contactUsPage.form.submittingButton")
              : getTranslation(i18n, "contactUsPage.form.submitButton")}
          </button>
          <button
            type="button"
            className={`${styles["form-button"]} ${styles["animated-submit-button"]} ${styles["form-field-5"]}`}
            onClick={() => navigate(-1)}
            disabled={isSubmitting}
          >
            {getTranslation(i18n, "contactUsPage.form.cancelButton")}
          </button>
        </form>
      </div>
    </div>
  );
}
